import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { useTheme } from '../../theme';
export const Card = forwardRef(({ variant = 'elevated', padding = 'md', interactive = false, disabled = false, children, style, className, ...props }, ref) => {
    const theme = useTheme();
    const getCardStyles = () => {
        const baseStyles = {
            borderRadius: theme.borderRadius.lg,
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden',
            opacity: disabled ? 0.6 : 1,
            cursor: interactive && !disabled ? 'pointer' : 'default',
        };
        // Padding styles
        const paddingStyles = {
            none: {
                padding: 0,
            },
            sm: {
                padding: theme.spacing.sm,
            },
            md: {
                padding: theme.spacing.md,
            },
            lg: {
                padding: theme.spacing.lg,
            },
        };
        // Variant styles
        const variantStyles = {
            elevated: {
                backgroundColor: theme.colors.surface,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                border: 'none',
            },
            outlined: {
                backgroundColor: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                boxShadow: 'none',
            },
            filled: {
                backgroundColor: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                boxShadow: 'none',
            },
        };
        return {
            ...baseStyles,
            ...variantStyles[variant],
            ...paddingStyles[padding],
            ...style,
        };
    };
    const getHoverStyles = () => {
        if (!interactive || disabled)
            return {};
        const hoverStyles = {
            elevated: {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
            },
            outlined: {
                borderColor: theme.colors.primary,
                boxShadow: `0 0 0 1px ${theme.colors.primary}`,
            },
            filled: {
                backgroundColor: theme.colors.surface,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
        };
        return hoverStyles[variant];
    };
    return (_jsx("div", { ref: ref, className: className, style: getCardStyles(), onMouseEnter: (e) => {
            if (interactive && !disabled) {
                Object.assign(e.currentTarget.style, {
                    ...getCardStyles(),
                    ...getHoverStyles(),
                });
            }
        }, onMouseLeave: (e) => {
            if (interactive && !disabled) {
                Object.assign(e.currentTarget.style, getCardStyles());
            }
        }, ...props, children: children }));
});
Card.displayName = 'Card';
export const CardHeader = forwardRef(({ children, style, ...props }, ref) => {
    const theme = useTheme();
    return (_jsx("div", { ref: ref, style: {
            paddingBottom: theme.spacing.sm,
            borderBottom: `1px solid ${theme.colors.border}`,
            marginBottom: theme.spacing.md,
            ...style,
        }, ...props, children: children }));
});
CardHeader.displayName = 'CardHeader';
export const CardBody = forwardRef(({ children, style, ...props }, ref) => {
    return (_jsx("div", { ref: ref, style: {
            flex: 1,
            ...style,
        }, ...props, children: children }));
});
CardBody.displayName = 'CardBody';
export const CardFooter = forwardRef(({ children, style, ...props }, ref) => {
    const theme = useTheme();
    return (_jsx("div", { ref: ref, style: {
            paddingTop: theme.spacing.sm,
            borderTop: `1px solid ${theme.colors.border}`,
            marginTop: theme.spacing.md,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing.sm,
            ...style,
        }, ...props, children: children }));
});
CardFooter.displayName = 'CardFooter';
